import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "../../config/Api";

import { PaymentDetails } from "../../types/orderTypes";


// ================= TYPES =================
export type AccountStatus =
  | "PENDING_VERIFICATION"
  | "ACTIVE"
  | "SUSPENDED"
  | "DEACTIVATED"
  | "BANNED"
  | "CLOSED";

export interface AdminUser {
  id: number;
  name?: string;
  sellerName?: string;
  email: string;
  mobile?: string;
  gstin?: string;
  businessName?: string;
  businessDetails?: {
    businessName?: string;
  };
  gstReport?: {
    gstin?: string;
  };
  role: "ROLE_CUSTOMER" | "ROLE_SELLER" | "ROLE_ADMIN";
  accountStatus: AccountStatus;
  createdAt?: string;
}

export interface MonthlyDataPoint {
  month: string;
  revenue: number;
  orders: number;
}

export interface CategoryDistributionPoint {
  name: string;
  value: number;
}

export interface AdminStats {
  totalUsers: number;
  totalSellers: number;
  totalOrders: number;
  totalRevenue: number;
  monthlyData?: MonthlyDataPoint[];
  categoryDistribution?: CategoryDistributionPoint[];
}

interface AdminState {
  users: AdminUser[];
  sellers: AdminUser[];
  products: any[];
  transactions: Transaction[];
  sponsoredProducts: any[];
  orders: any[];
  stats: AdminStats | null;
  loading: boolean;
  error: string | null;
  statusUpdated: boolean;
}

const initialState: AdminState = {
  users: [],
  sellers: [],
  products: [],
  transactions: [],
  sponsoredProducts: [],
  orders: [],
  stats: null,
  loading: false,
  error: null,
  statusUpdated: false,
};


export interface Transaction {
    id: number;
    transactionId?: string | null;
    amount: number;
    date: string | number[];

    customer?: {
        name?: string;
        email?: string;
    } | null;

    seller: {
        id: number;
        businessDetails?: {
            businessName?: string;
        };
        sellerName?: string;
        email: string;
    };

    order?: {
        id: number;
        totalSellingPrice?: number;
        paymentStatus?: string;
        paymentDetails?: PaymentDetails;
    } | null;
}

// Helper function to build headers cleanly
const getAuthHeader = (jwt?: string | null | void) => {
  const token = typeof jwt === "string" ? jwt : localStorage.getItem("jwt");
  if (!token) return {};
  return {
    headers: {
      Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    },
  };
};

// ================= THUNKS =================

// 1. Fetch All Users
export const fetchAllUsers = createAsyncThunk<
  AdminUser[],
  string | void,
  { rejectValue: string }
>("admin/fetchAllUsers", async (jwt, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    const response = await api.get("/api/admin/users", config);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch users"
    );
  }
});

// 2. Fetch All Sellers
export const fetchAllSellers = createAsyncThunk<
  AdminUser[],
  string | void,
  { rejectValue: string }
>("admin/fetchAllSellers", async (jwt, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    const response = await api.get("/api/admin/sellers", config);

    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.content)) {
      return data.content;
    } else if (data && Array.isArray(data.sellers)) {
      return data.sellers;
    }

    return [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch sellers"
    );
  }
});

// 3. Update User/Seller Account Status
export const updateUserStatus = createAsyncThunk<
  AdminUser,
  { userId: number; status: AccountStatus; role?: string; jwt?: string | null },
  { rejectValue: string }
>(
  "admin/updateUserStatus",
  async ({ userId, status, role, jwt }, { rejectWithValue }) => {
    try {
      const config = getAuthHeader(jwt);
      const endpoint =
        role === "ROLE_SELLER"
          ? `/api/admin/sellers/${userId}/status`
          : `/api/admin/users/${userId}/status`;

      const response = await api.patch(endpoint, { status }, config);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update account status"
      );
    }
  }
);

// 4. Delete User
export const deleteUserByAdmin = createAsyncThunk<
  number,
  { userId: number; jwt?: string | null },
  { rejectValue: string }
>("admin/deleteUserByAdmin", async ({ userId, jwt }, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    await api.delete(`/api/admin/users/${userId}`, config);
    return userId;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete user"
    );
  }
});

// 5. Fetch Dashboard Stats
export const fetchAdminDashboardStats = createAsyncThunk<
  AdminStats,
  string | void,
  { rejectValue: string }
>("admin/fetchDashboardStats", async (jwt, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    const response = await api.get("/api/admin/dashboard/stats", config);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to load dashboard stats"
    );
  }
});

// 6. Fetch All Admin Products
export const fetchAllAdminProducts = createAsyncThunk<
  any[],
  string | void,
  { rejectValue: string }
>("admin/fetchAllAdminProducts", async (jwt, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    const response = await api.get("/api/admin/products", config);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch products"
    );
  }
});

export const fetchAllAdminOrders = createAsyncThunk<
  any[],
  string | void,
  { rejectValue: string }
>("admin/fetchAllAdminOrders", async (jwt, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    const response = await api.get("/api/admin/orders", config);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch orders"
    );
  }
});

export const updateAdminOrderStatus = createAsyncThunk<
  any,
  { orderId: number; orderStatus: string; jwt?: string | null },
  { rejectValue: string }
>(
  "admin/updateAdminOrderStatus",
  async ({ orderId, orderStatus, jwt }, { rejectWithValue }) => {
    try {
      const config = getAuthHeader(jwt);
      const response = await api.patch(
        `/api/admin/orders/${orderId}/status`,
        { orderStatus },
        config
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update order status"
      );
    }
  }
);

export const fetchAdminSponsoredProducts = createAsyncThunk<
  any[],
  string | void,
  { rejectValue: string }
>("admin/fetchAdminSponsoredProducts", async (jwt, { rejectWithValue }) => {
  try {
    const config = getAuthHeader(jwt);
    const response = await api.get("/api/admin/sponsored-products", config);
    const data = response.data;

    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.content)) {
      return data.content;
    } else if (data && Array.isArray(data.sponsoredProducts)) {
      return data.sponsoredProducts;
    }

    return [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch sponsored products"
    );
  }
}
);

export const fetchAllTransactions = createAsyncThunk<
  Transaction[],
  string | undefined,
  { rejectValue: string }
>(
  "admin/fetchAllTransactions",
  async (jwt, { rejectWithValue }) => {
    try {
      const response = await api.get("/api/admin/transactions", getAuthHeader(jwt));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch transactions");
    }
  }
);

// ================= SLICE =================

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    resetAdminStatus: (state) => {
      state.statusUpdated = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllUsers.fulfilled,
        (state, action: PayloadAction<AdminUser[]>) => {
          state.loading = false;
          state.users = action.payload;
        }
      )
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch users";
      })

      // Fetch All Sellers
      .addCase(fetchAllSellers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllSellers.fulfilled,
        (state, action: PayloadAction<AdminUser[]>) => {
          state.loading = false;
          state.sellers = action.payload;
        }
      )
      .addCase(fetchAllSellers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch sellers";
      })

      // Update User Status
      .addCase(updateUserStatus.pending, (state) => {
        state.loading = true;
        state.statusUpdated = false;
        state.error = null;
      })
      .addCase(
        updateUserStatus.fulfilled,
        (state, action: PayloadAction<AdminUser>) => {
          state.loading = false;
          state.statusUpdated = true;
          const updatedUser = action.payload;

          state.users = state.users.map((u) =>
            u.id === updatedUser.id ? { ...u, ...updatedUser } : u
          );
          state.sellers = state.sellers.map((s) =>
            s.id === updatedUser.id ? { ...s, ...updatedUser } : s
          );
        }
      )
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update user status";
      })

      // Delete User
      .addCase(deleteUserByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteUserByAdmin.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.users = state.users.filter((u) => u.id !== action.payload);
          state.sellers = state.sellers.filter((s) => s.id !== action.payload);
        }
      )
      .addCase(deleteUserByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete user";
      })

      // Dashboard Stats
      .addCase(fetchAdminDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAdminDashboardStats.fulfilled,
        (state, action: PayloadAction<AdminStats>) => {
          state.loading = false;
          state.stats = action.payload;
        }
      )
      .addCase(fetchAdminDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load dashboard stats";
      })

      // Fetch All Admin Products
      .addCase(fetchAllAdminProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllAdminProducts.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.products = action.payload;
        }
      )
      .addCase(fetchAllAdminProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch products";
      }
      )
      .addCase(fetchAllAdminOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllAdminOrders.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.orders = action.payload;
        }
      )
      .addCase(fetchAllAdminOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch orders";
      })

      // Update Order Status
      .addCase(updateAdminOrderStatus.fulfilled, (state, action) => {
        const updatedOrder = action.payload;
        state.orders = state.orders.map((o) =>
          o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o
        );
      })
      .addCase(fetchAdminSponsoredProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminSponsoredProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.sponsoredProducts = action.payload;
      })
      .addCase(fetchAdminSponsoredProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAllTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTransactions.fulfilled, (state, action: PayloadAction<Transaction[]>) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchAllTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetAdminStatus } = adminSlice.actions;
export default adminSlice.reducer;